import axios from "axios";
import ApiJson from "./apiJson";
import {
  showErrorToast,
  showSuccessToast,
  getLocalStorage,
  setLocalStorage,
} from "../helpers/Utils";
import CONSTANTS from "../helpers/Constants";

let apiFailCounter = 0;
const MAX_RETRIES = 3;

// Function to refresh token
let refreshingTokenPromise = null
export const refreshToken = async (userInfo) => {
  if (!refreshingTokenPromise) {
    refreshingTokenPromise = (async () => {
      try {
        const response = await axios.post(
          `${CONSTANTS.searchUrl}/app/getAccessToken`,
          { refreshToken: userInfo.refreshToken, roleId: userInfo.roleId },
          {
            headers: {
              Authorization: `Spike ${userInfo.token}`,
            },
          }
        )

        refreshingTokenPromise = null

        
        // update LocalStorage
        const responseData = response.data.result;
        setLocalStorage('userInfo', {
          ...userInfo,
          token: responseData.token,
          refreshToken: responseData.refreshToken,
          tokenExpiresIn: responseData.tokenExpiresIn,
          refreshTokenExpiresIn: responseData.refreshTokenExpiresIn
        })

        return responseData
      } catch (error) {
        refreshingTokenPromise = null

        if (error?.response?.status === 401) {
          showErrorToast(
            'Your session has expired. Please log in again to continue.'
          )
          localStorage.clear();
          window.open(window.location.origin, "_self");
        }

        return Promise.reject(error.response || { data: {} })
      }
    })()
  }

  return refreshingTokenPromise
}

export const axiosInstance = axios.create()
axiosInstance.defaults.baseURL = CONSTANTS.searchUrl

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const userInfo = getLocalStorage("userInfo");
    if (userInfo && userInfo.token) {
      config.headers.Authorization = `Spike ${userInfo.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor with Retry Logic
axiosInstance.interceptors.response.use(
  (response) => {
    apiFailCounter = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle retry for network errors or server errors (5xx)
    if (
      !originalRequest._retry &&
      (error.toJSON().message === "Network Error" || (error.response && error.response.status >= 500))
    ) {
      originalRequest._retry = true; // Mark request as retried
      if (apiFailCounter < MAX_RETRIES) {
        apiFailCounter++;
        return axiosInstance(originalRequest); // Retry the request
      }
    }

    // Handle unauthorized (401) errors
    if (error.response && error.response.status === 401) {
      showErrorToast(
        'Your session has expired. Please log in again to continue.'
      )
      localStorage.clear();
      window.open(window.location.origin, "_self");
    }

    // Show error toast for non-retriable errors
    if (error.response && error.config.showErrorMessage) {
      handleErrorByStatus(error.response.data || error.response);
    } else if (error.toJSON().message === "Network Error") {
      showErrorToast(
        "We are experiencing some network issues. Please check your internet connection."
      );
    }

    // Clear local storage and redirect after max retries
    if (apiFailCounter >= MAX_RETRIES) {
    }
    
    return Promise.reject(error);
  }
);

// Utility functions
const prepareDataObject = (_data_, paramObj) => {
  for (let key in _data_) {
    if (paramObj[key] || paramObj[key] === false || paramObj[key] === 0) {
      _data_[key] = paramObj[key];
    } else {
      if (typeof _data_[key] !== "object") _data_[key] = "";
    }
  }
  return _data_;
};

const injectParamsToUrl = (_url_, paramObj) => {
  let url = _url_;
  for (let key in paramObj) {
    url = url.replace(`:${key}`, paramObj[key]);
  }
  return url;
};

const handleErrorByStatus = (error) => {
  if (error && error.status === "Error") {
    const message = error.message;
    showErrorToast(message);
  }
};

// Main API Service
const spikeViewApiService = async(apiKeyName, data) => {
  const userInfo = getLocalStorage("userInfo");
  if (userInfo && (userInfo?.tokenExpiresIn ?? 0) <= new Date().getTime()) {
    await refreshToken(userInfo)
  }

  const apiDetails = ApiJson[apiKeyName];
  if (!apiDetails) {
    throw new Error("API configuration missing in api-json.js.");
  }

  const requestObject = { ...apiDetails, data: prepareDataObject(apiDetails.data, data) };

  if (apiKeyName === "schoolUpdate") {
    requestObject.data = data; // Special case for schoolUpdate
  }

  requestObject.url = injectParamsToUrl(requestObject.url, data);

  return axiosInstance(requestObject)
    .then((result) => {
      if (result.data?.status === "Success") {
        if (result.data.message && requestObject.showResultMessage){
          showSuccessToast(result.data.message);
        }
      } else {
        handleErrorByStatus(result.data);
        return Promise.reject(result.data);
      }

      return result;
    })
    .catch((error) => {
      return Promise.reject(error); // Let the interceptor handle retries
    });
};

export default spikeViewApiService;
